import { SmallBody } from "helpers/TypographyComponents";
import type { ReactNode } from "react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useAppDispatch, useAppSelector } from "store/store";

import styles from "../style.module.scss";
import SalesComparisonChart from "./SalesComparisonChart";
import type { AnalyticsLayout, StoredPreferences } from "./utils";
import { getStoredLayoutOrDefault, settingsTransform, useFilterOptions } from "./utils";
const ResponsiveGridLayout = WidthProvider(Responsive);
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./react-grid-layout-overriden-placeholder.scss";
import "./chart-js.style.scss";

import CachedRoundedIcon from "@mui/icons-material/CachedRounded";
import classNames from "classnames";
import type { Action } from "components/shared/ButtonMenu";
import ButtonMenu from "components/shared/ButtonMenu";
import LoadingWrapper from "components/shared/LoadingWrapper";
import StyledButton from "components/shared/StyledButton";
import { formatLocaleDateString, getJsonOrFalse, isObjectEmpty } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useAnalyticsJobs } from "hooks/useJobs";
import useMounted from "hooks/useMounted";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";
import type { FetchSalesParams, GroupMethod } from "store/analyticsStore";
import { fetchAnalyticsCountries, refreshSalesAnalytics } from "store/analyticsStore";
import type { TPeriod } from "store/periodStore";
import { fetchCompanyInfo, putCompanySettings } from "store/settingsStore";

import { Layout } from "../Page";
import ChannelsMetrics from "./ChannelsMetrics";
import Indicators from "./Indicators";
import TopPerformingDataBlock from "./TopPerformingDataBlock";

export type AnalyticsRefreshContext = {
    needsRecompute: boolean;
    reason: "sales_new" | "sales_delete" | "sales_import" | "data_compute" | "";
    date: Date | string | null;
    lastRefresh: Date | string | null;
};

const BY_MONTH_CHART_GROUPING: GroupMethod[] = ["perYear", "perMonth"];
const BY_CHANNEL_CHART_GROUPING: GroupMethod[] = ["perChannel"];
const BY_COUNTRY_CHART_GROUPING: GroupMethod[] = ["perCountry"];
const NO_GROUPING: GroupMethod[] = [];

/**
 * Analytics page Dashboard component
 */
const AnalyticsDashboardContent = forwardRef<{ resetLayout: () => void }, { isEditingGrid: boolean }>(
    ({ isEditingGrid }, ref) => {
        const dispatch = useAppDispatch();
        const { t } = useTranslation();
        const { getCompanySettings, companyInfos } = useCompanySettings();
        const { periods, currentPeriod, periodsDateRange } = useAppSelector((state) => state.period);
        const { analyticsDashboard, analyticsCountries, analyticsCountriesStatus, dashboardStatus } = useAppSelector(
            (state) => state.analytics,
        );
        const { channels } = useAppSelector((state) => state.channels);
        const { titlesAutocomplete } = useAppSelector((state) => state.titles);
        const [isDragging, setIsDragging] = useState(false);

        const storedPreferences = useMemo(
            () => getCompanySettings("analytics_layout") as StoredPreferences,
            [companyInfos],
        );

        useEffect(() => {
            if (isObjectEmpty(periodsDateRange)) return;
            const { startDate, endDate } = periodsDateRange;
            const params = { startDate, endDate, groupMethod: "perMonth" } as FetchSalesParams;

            if (analyticsCountriesStatus === "idle") {
                dispatch(fetchAnalyticsCountries({ params }));
            }
        }, [periodsDateRange]);

        const mounted = useMounted(1000);
        const filterOptions = useFilterOptions(titlesAutocomplete, channels, analyticsCountries);

        const {
            handleQuery: _handleQueryTitle,
            setOffsetPagination: setOffsetPaginationTitles,
            requestStatus: requestStatusTitle,
        } = useAPIPagination({
            type: "titles",
            defaultLimitPagination: 10,
            portal: "book",
            autocomplete: true,
        });

        const handleQueryTitleRef = useRef(_handleQueryTitle);
        handleQueryTitleRef.current = _handleQueryTitle;

        useImperativeHandle(ref, () => ({
            resetLayout: () => {
                setLayout(defaultLayout);
            },
        }));

        // initial layout state, before registered layouts position in company settings
        const defaultLayout: AnalyticsLayout[] = useMemo(
            () => [
                {
                    component: (
                        <Indicators
                            dashboard={analyticsDashboard["indicators"]}
                            storedPreferences={storedPreferences}
                            periodsDateRange={periodsDateRange}
                            showVsN_1Label={periods?.length > 1}
                            fetchStatus={dashboardStatus["indicators"]}
                            type="indicators"
                        />
                    ),
                    layout: {
                        w: 12,
                        h: 6,
                        x: 0,
                        y: 0,
                        i: "indicators",
                        minW: 4,
                        minH: 6,
                    },
                },
                {
                    component: (
                        <SalesComparisonChart
                            label={t("Analytics.EvolutionFormatCountry")}
                            dataTypeLabel="Y-Axis"
                            filterOptions={filterOptions}
                            storedPreferences={storedPreferences}
                            titlesPaginationData={{
                                handleQueryTitle: handleQueryTitleRef.current,
                                setOffsetPaginationTitles,
                                requestStatusTitle,
                            }}
                            periodsDateRange={periodsDateRange}
                            type="line-chart"
                            groupMethods={BY_MONTH_CHART_GROUPING}
                        />
                    ),
                    layout: {
                        w: 12,
                        h: 17,
                        x: 0,
                        y: 20,
                        i: "line-chart",
                        minW: 5,
                        minH: 12,
                    },
                },
                {
                    layout: {
                        w: 5,
                        h: 14,
                        x: 0,
                        y: 6,
                        i: "top-performing-data",
                        minW: 4,
                        minH: 6,
                    },
                    component: (
                        <TopPerformingDataBlock
                            dashboard={analyticsDashboard["top-performing-data"]}
                            storedPreferences={storedPreferences}
                            periodsDateRange={periodsDateRange}
                            fetchStatus={dashboardStatus["top-performing-data"]}
                            type="top-performing-data"
                        />
                    ),
                },
                {
                    layout: {
                        w: 6,
                        h: 17,
                        x: 0,
                        y: 37,
                        i: "pie-chart-per-country",
                        minW: 3,
                        minH: 12,
                    },
                    component: (
                        <SalesComparisonChart
                            label={t("Analytics.DistributionCountry")}
                            dataTypeLabel="Base :"
                            filterOptions={filterOptions}
                            storedPreferences={storedPreferences}
                            titlesPaginationData={{
                                handleQueryTitle: handleQueryTitleRef.current,
                                setOffsetPaginationTitles,
                                requestStatusTitle,
                            }}
                            periodsDateRange={periodsDateRange}
                            type="pie-chart-per-country"
                            groupMethods={BY_COUNTRY_CHART_GROUPING}
                        />
                    ),
                },
                {
                    layout: {
                        w: 12,
                        h: 18,
                        x: 0,
                        y: 54,
                        i: "bar-chart",
                        minW: 5,
                        minH: 12,
                    },
                    component: (
                        <SalesComparisonChart
                            label={t("Analytics.AnalysisFormatCountry")}
                            dataTypeLabel="Y-Axis"
                            filterOptions={filterOptions}
                            storedPreferences={storedPreferences}
                            titlesPaginationData={{
                                handleQueryTitle: handleQueryTitleRef.current,
                                setOffsetPaginationTitles,
                                requestStatusTitle,
                            }}
                            periodsDateRange={periodsDateRange}
                            groupMethods={NO_GROUPING}
                            type="bar-chart"
                        />
                    ),
                },
                {
                    layout: {
                        w: 6,
                        h: 17,
                        x: 6,
                        y: 37,
                        i: "pie-chart-per-channel",
                        minW: 3,
                        minH: 12,
                    },
                    component: (
                        <SalesComparisonChart
                            label={t("Analytics.DistributionFormat")}
                            dataTypeLabel="Base :"
                            filterOptions={filterOptions}
                            storedPreferences={storedPreferences}
                            titlesPaginationData={{
                                handleQueryTitle: handleQueryTitleRef.current,
                                setOffsetPaginationTitles,
                                requestStatusTitle,
                            }}
                            periodsDateRange={periodsDateRange}
                            type="pie-chart-per-channel"
                            groupMethods={BY_CHANNEL_CHART_GROUPING}
                        />
                    ),
                },
                {
                    layout: {
                        w: 7,
                        h: 14,
                        x: 5,
                        y: 6,
                        i: "channels-metrics",
                        minW: 3,
                        minH: 12,
                    },
                    component: (
                        <ChannelsMetrics
                            currentPeriod={currentPeriod as TPeriod}
                            storedPreferences={storedPreferences}
                            periodsDateRange={periodsDateRange}
                            titleOptions={filterOptions.titles}
                            titlesPaginationData={{
                                handleQueryTitle: handleQueryTitleRef.current,
                                setOffsetPaginationTitles,
                                requestStatusTitle,
                            }}
                            type="channels-metrics"
                        />
                    ),
                },
            ],
            [
                analyticsDashboard,
                storedPreferences,
                periodsDateRange,
                periods?.length,
                dashboardStatus,
                t,
                filterOptions,
                setOffsetPaginationTitles,
                requestStatusTitle,
                currentPeriod,
            ],
        );

        // get layout from local storage or default layout
        const initialLayout: AnalyticsLayout[] = getStoredLayoutOrDefault(storedPreferences, defaultLayout);

        const [layout, setLayout] = useState(initialLayout);

        // update layout when props changes
        useEffect(() => {
            setLayout(initialLayout);
        }, [defaultLayout]);

        const layouts = useMemo(() => ({ lg: layout.map((item) => item.layout) }), [layout]);

        /**
         * Save layout to company settings
         */
        const handleSaveLayout = (newLayout) => {
            /**
             * Save filter preferences when component is mounted
             * it avoid useless fetch for company settings at first load
             */
            if (!mounted) return;
            const updatedLayout = initialLayout.map((item, index) => ({
                ...item,
                layout: newLayout[index] || item.layout,
            }));
            const settingsValue = settingsTransform("layout", updatedLayout, storedPreferences);
            dispatch(putCompanySettings([{ settingsName: "analytics_layout", settingsValue }]));
        };

        return (
            <div className="py-[20px]!">
                {/** GRID CHARTS LAYOUT */}
                <LoadingWrapper loading={!currentPeriod} type="loading-page">
                    <ResponsiveGridLayout
                        className={styles["layout"]}
                        layouts={layouts}
                        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                        rowHeight={15}
                        containerPadding={[0, 0]}
                        margin={[15, 15]}
                        onLayoutChange={(newLayout) => handleSaveLayout(newLayout)} // after moving or resizing a block
                        isDraggable={isEditingGrid}
                        isResizable={isEditingGrid}
                        onDragStart={() => setIsDragging(true)}
                        onDragStop={() => setIsDragging(false)}
                    >
                        {layout.map((item) => (
                            <div
                                key={item.layout.i}
                                data-grid={item.layout}
                                className={classNames({
                                    [styles["graph-block"]]: true,
                                    [styles["is-editing"]]: isEditingGrid,
                                    [styles["is-dragging"]]: isDragging,
                                })}
                            >
                                {item.component}
                            </div>
                        ))}
                    </ResponsiveGridLayout>
                </LoadingWrapper>
            </div>
        );
    },
);

function AnalyticsDashboard({ tabsContainer }: { tabsContainer: ReactNode }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings, companyInfos } = useCompanySettings();
    const { refreshAnalyticsStatus } = useAppSelector((state) => state.analytics);
    const { analyticsJobs } = useAppSelector((state) => state.jobs);
    useAnalyticsJobs(true);

    const [isEditingGrid, setIsEditingGrid] = useState(false);

    const contentRef = useRef<{ resetLayout: () => void }>(null);

    // Used to force reload the AnalyticsDashboardContent component
    const [refreshKey, setRefreshKey] = useState(Date.now());

    const lang = getJsonOrFalse(localStorage.getItem("settings") ?? "{}").accountSettings?.language || "fr";

    const refreshContext: AnalyticsRefreshContext = useMemo(
        () => getCompanySettings("analytics_refresh_context"),
        [companyInfos],
    );

    /**
     * We need to get updated lastRefresh date that is stored in company settings : analytics_refresh_context
     */
    useEffect(() => {
        dispatch(fetchCompanyInfo());
    }, []); // fetch at each page render

    const refreshedAt = refreshContext?.lastRefresh ?? null;
    const lastRefresh = refreshedAt ? moment(refreshedAt).locale(lang).fromNow() : null;

    /**
     * Render reload Analytics button tooltip infos
     * 1) Refreshing analytics (me)
     * 2) Computing data (computing job - large data company)
     * 3) Should refresh, data not up to date
     */
    const renderReloadBtnTooltip = useMemo(() => {
        if (refreshAnalyticsStatus === "pending") {
            return t("Analytics.Refreshing");
        }
        if (analyticsJobs.computing) {
            return t("Analytics.Computing");
        }
        if (refreshContext?.needsRecompute && refreshContext?.date) {
            const date = formatLocaleDateString({
                date: refreshContext?.date,
                options: { numeric: true, showTime: true, timeZone: "UTC" },
            });
            const renderReasonHtml = (reason: string, date: string) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{t("Analytics.LastModified")}</span>
                    <span>{`${reason} - ${date}`}</span>
                </div>
            );
            return renderReasonHtml(t(`Analytics.RefreshReason_${refreshContext.reason}`), date);
        }
        return "";
    }, [refreshAnalyticsStatus, analyticsJobs, refreshContext]);

    const btnActions: Action[] = [
        {
            name: t("Analytics.ResetGrid"),
            action: () => {
                contentRef.current?.resetLayout?.();
            },
        },
        {
            name: <SmallBody>{t("Analytics.NewDiagram")}</SmallBody>,
            disabled: true,
            subtitle: t("Analytics.ComingSoon"),
        },
    ];

    // Auto-refresh analytics if data is stale (> 2 days).
    useEffect(() => {
        if (!refreshedAt || refreshAnalyticsStatus === "pending") return;
        const daysSinceRefresh = moment(refreshedAt).diff(moment(), "days");
        if (daysSinceRefresh < -2) {
            dispatch(refreshSalesAnalytics()).then(() => {
                setRefreshKey(Date.now());
                dispatch(fetchCompanyInfo());
            });
        }
    }, [dispatch, refreshedAt]);

    return (
        <Layout
            content={<AnalyticsDashboardContent key={refreshKey} ref={contentRef} isEditingGrid={isEditingGrid} />}
            tabsContainer={tabsContainer}
            header={
                <div className={classNames(styles["header"])}>
                    <div className={styles["header-left"]}>
                        {lastRefresh && (
                            <span className={styles["last-refresh"]}>
                                {t("Analytics.LastUpdate")} {lastRefresh}
                            </span>
                        )}
                    </div>
                    <div className={styles["header-right"]}>
                        <div className={styles["actions"]}>
                            <StyledButton
                                buttonType={refreshContext?.needsRecompute ? "outlined" : "secondary"}
                                tooltipText={renderReloadBtnTooltip}
                                onClick={() => {
                                    dispatch(refreshSalesAnalytics()).then(() => {
                                        setRefreshKey(Date.now());
                                        dispatch(fetchCompanyInfo());
                                    });
                                }}
                                disabled={refreshAnalyticsStatus === "pending" || analyticsJobs.computing}
                            >
                                <CachedRoundedIcon sx={{ width: "16px", height: "16px" }} />
                                {t("Analytics.Refresh")}
                            </StyledButton>
                            <ButtonMenu
                                buttonType="primary"
                                actions={btnActions}
                                mainActions={{
                                    name: !isEditingGrid ? t("Analytics.Edit") : t("Analytics.Save"),
                                    action: () => setIsEditingGrid(!isEditingGrid),
                                }}
                                fullWidth
                            />
                        </div>
                        {refreshContext?.needsRecompute ? (
                            <span className={"text-orange-opaque text-[12px]! leading-[0]! whitespace-nowrap"}>
                                {t("Analytics.NeedsRecompute")}
                            </span>
                        ) : null}
                    </div>
                </div>
            }
        />
    );
}

AnalyticsDashboardContent.displayName = "AnalyticsDashboardContent";

export default AnalyticsDashboard;
